import moment from 'moment';
import { Department, IDepartment } from '../department';
import { IUser, User } from '../user';
import {
  IWorkflowFormFieldSubmission,
  IWorkflowFormFieldSubmissionData,
  IWorkflowFormSubmission,
  IWorkflowFormSubmissionData,
  IWorkflowFormFieldOptionSubmission,
  IWorkflowFormFieldOptionSubmissionData,
  IWorkflowFormFieldSubmissionDataFieldErrorData,
  IWorkflowFormFieldSubmissionDataFieldError,
  IWorkflowFormFieldOptionSubmissionFieldErrorData,
  IWorkflowFormFieldOptionSubmissionFieldError,
  IWorkflowFormSubmissionStateInfoData,
  IWorkflowFormSubmissionChecklist,
  IWorkflowFormSubmissionChecklistData,
  IWorkflowFormSubmissionChecklistToDoTemplateData,
  IWorkflowFormSubmissionChecklistToDoTemplate,
  IWorkflowFormSubmissionComment,
  IWorkflowFormSubmissionCommentData,
  IWorkflowFormSubmissionCommentFieldError,
  IWorkflowFormSubmissionFieldError,
  IWorkflowFormSubmissionFieldErrorData,
  IWorkflowFormSubmissionCommentFieldErrorData,
  WorkflowFormSubmissionChecklistStateEnum,
  IWorkflowFormSubmissionChecklistStateInfo,
  IWorkflowFormSubmissionCommentAttachment,
  IWorkflowFormSubmissionCommentAttachmentData
} from './workflow-form.types';
import { MentionCommentMixin } from '../mention';
import {
  IWorkflowFormField,
  IWorkflowFormSectionField,
  IWorkflowFormTemplateRevision,
  NO_OPTIONS_FIELD_TYPES,
  WorkflowConstraintActionEnum,
  WorkflowFormFieldTypeEnum,
  WorkflowFormSectionField,
  WorkflowFormTemplateRevision,
  IWorkflowFormTemplate
} from '../workflow-form-template';
import { WorkflowState } from '../workflow/workflow';
import { IWorkflowState } from '../workflow/workflow.types';
import { AbstractModel } from '../base';
import { AbstractAttachment, IAttachmentData } from '../attachment';

export class WorkflowFormSubmission extends AbstractModel
  implements IWorkflowFormSubmission {
  readonly id?: number;
  readonly form: IWorkflowFormTemplateRevision | number;
  readonly assignee: IUser | null = null;
  readonly answers: IWorkflowFormFieldSubmission[] = [];
  readonly state?: IWorkflowState;
  readonly owner?: string;
  readonly number?: number;
  readonly workflow?: number;
  readonly meta_fields: Record<string, string> = {};
  readonly created_at?: string | Date;
  readonly updated_at?: string | Date;
  readonly series_id: string = '';

  constructor(data: IWorkflowFormSubmissionData) {
    super();

    this.validate(data, ['form']);

    if (data.id) {
      this.id = data.id;
    }

    if (data.assignee) {
      this.assignee = data.assignee ? new User(data.assignee) : null;
    }

    this.form =
      typeof data.form == 'number'
        ? data.form
        : new WorkflowFormTemplateRevision(data.form!);

    if (data.state) {
      this.state = new WorkflowState(data.state);
    }

    if (data.answers) {
      this.answers = data.answers.map(
        data => new WorkflowFormFieldSubmission(data)
      );
    }

    this.owner = data.owner ?? '';

    if (data.workflow) {
      this.workflow = data.workflow;
    }

    if (data.number) {
      this.number = data.number;
    }

    if (data.meta_fields) {
      this.meta_fields = data.meta_fields;
    }

    if (data.series_id) {
      this.series_id = data.series_id;
    }

    if (data.created_at) {
      this.created_at = data.created_at ?? '';
    }
    if (data.updated_at) {
      this.updated_at = data.updated_at ?? '';
    }
  }

  hasAnyUrgentAnswers(): boolean {
    return this.answers.some(answer => answer.is_urgent);
  }
}

export class WorkflowFormFieldSubmissionAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}
export class WorkflowFormFieldSubmission
  implements IWorkflowFormFieldSubmission {
  readonly id?: number;
  readonly value: string;
  readonly field: number | IWorkflowFormSectionField;
  readonly option_submissions: (IWorkflowFormFieldOptionSubmission | number)[];
  readonly attachments: WorkflowFormFieldSubmissionAttachment[] = [];
  readonly is_urgent: boolean = false;
  readonly template_index: number;

  constructor(data: IWorkflowFormFieldSubmissionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.value = data.value;

    if (typeof data.field == 'number') {
      this.field = data.field;
    } else {
      this.field = new WorkflowFormSectionField(data.field);
    }

    this.option_submissions = data.option_submissions.map(data => {
      if (typeof data === 'number') {
        return data;
      }
      return new WorkflowFormFieldOptionSubmission(data);
    });

    if (data.attachments) {
      this.attachments = data.attachments.map(
        attachment => new WorkflowFormFieldSubmissionAttachment(attachment)
      );
    }

    if (data.is_urgent) {
      this.is_urgent = data.is_urgent;
    }

    this.template_index = data.template_index ?? 0;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  isRequired(answers: IWorkflowFormFieldSubmission[]): boolean {
    let required = false;
    const constraints = (this.field as IWorkflowFormSectionField).constraints;

    if (
      constraints
        .filter(
          constraint =>
            constraint.action === WorkflowConstraintActionEnum.Required
        )
        .some(constraint => constraint.isSatisfied(answers))
    ) {
      required = true;
    } else if (
      constraints
        .filter(constraint =>
          [
            WorkflowConstraintActionEnum.Optional,
            WorkflowConstraintActionEnum.Disabled,
            WorkflowConstraintActionEnum.Hidden
          ].includes(constraint.action)
        )
        .some(constraint => constraint.isSatisfied(answers))
    ) {
      required = false;
    } else {
      required = (this.field as IWorkflowFormSectionField).required;
    }

    return required;
  }

  isAnswered(): boolean {
    const fieldTemplate = (this.field as IWorkflowFormSectionField)
      ?.field_template as IWorkflowFormField;
    if (!fieldTemplate) {
      throw new Error('Invalid field or field_template type');
    }

    const fieldType = fieldTemplate.type;

    if (NO_OPTIONS_FIELD_TYPES.includes(fieldType)) {
      return (
        ![
          WorkflowFormFieldTypeEnum.Spacer,
          WorkflowFormFieldTypeEnum.CalculationField
        ].includes(fieldType) && this.value.trim() !== ''
      );
    }

    return this.option_submissions.length > 0;
  }

  getValue() {
    const fieldTemplate = (this.field as IWorkflowFormSectionField)
      ?.field_template as IWorkflowFormField;

    if (this.value) return this.value;

    if (NO_OPTIONS_FIELD_TYPES.includes(fieldTemplate.type)) {
      if (fieldTemplate.type === WorkflowFormFieldTypeEnum.MultiFileField) {
        return this.attachments;
      } else {
        return this.value;
      }
    } else {
      return this.option_submissions
        .map(option => option.toString())
        .join(', ');
    }
  }
}

export class WorkflowFormFieldOptionSubmission
  implements IWorkflowFormFieldOptionSubmission {
  readonly id?: number;
  readonly option: number;

  constructor(data: IWorkflowFormFieldOptionSubmissionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.option = data.option;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toString() {
    return this.option;
  }
}

export class WorkflowFormSubmissionChecklist
  implements IWorkflowFormSubmissionChecklist {
  readonly id?: number;
  readonly assignee: User | number;
  readonly todo_template: IWorkflowFormSubmissionChecklistToDoTemplate;
  readonly form_submission: number;
  readonly description: string;
  readonly state: WorkflowFormSubmissionChecklistStateEnum =
    WorkflowFormSubmissionChecklistStateEnum.InProgress;
  readonly completed_at: string | null;
  readonly completed_by: string | null;

  constructor(data: IWorkflowFormSubmissionChecklistData) {
    if (data.id) {
      this.id = data.id;
    }

    this.todo_template = new WorkflowFormSubmissionChecklistToDoTemplate(
      data.todo_template
    );

    if (typeof data.assignee == 'number') {
      this.assignee = data.assignee;
    } else {
      this.assignee = new User(data.assignee);
    }

    this.form_submission = data.form_submission;
    this.description = data.description ?? '';

    if (data.state) {
      this.state = data.state;
    }

    this.completed_at = data.completed_at ?? null;
    this.completed_by = data.completed_by ?? null;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  getChecklistStateInfo(): IWorkflowFormSubmissionChecklistStateInfo {
    return new WorkflowFormSubmissionChecklistStateInfo({
      value: this.state
    }).getFormSubmissionChecklistState();
  }
}

export class WorkflowFormSubmissionChecklistStateInfo
  implements IWorkflowFormSubmissionChecklistStateInfo {
  readonly value: WorkflowFormSubmissionChecklistStateEnum;
  readonly text?: string;
  readonly icon?: string;
  readonly color?: string;

  constructor(data: IWorkflowFormSubmissionChecklistStateInfo) {
    this.value = data.value;
    if (data.text) {
      this.text = data.text;
    }
    if (data.icon) {
      this.icon = data.icon;
    }
    if (data.color) {
      this.color = data.color;
    }
  }

  static InProgress = new WorkflowFormSubmissionChecklistStateInfo({
    icon: 'mdi-circle-outline',
    text: 'Mark To Do',
    color: 'grey darken-1',
    value: WorkflowFormSubmissionChecklistStateEnum.InProgress
  });

  static NotApplicable = new WorkflowFormSubmissionChecklistStateInfo({
    icon: 'mdi-minus-circle',
    text: 'Mark Not Applicable',
    color: 'error',
    value: WorkflowFormSubmissionChecklistStateEnum.NotApplicable
  });

  static Completed = new WorkflowFormSubmissionChecklistStateInfo({
    icon: 'mdi-check-circle',
    text: 'Mark Completed',
    color: 'success',
    value: WorkflowFormSubmissionChecklistStateEnum.Completed
  });

  getFormSubmissionChecklistState(): IWorkflowFormSubmissionChecklistStateInfo {
    let stateInfo = null;
    switch (this.value) {
      case WorkflowFormSubmissionChecklistStateEnum.Completed:
        stateInfo = WorkflowFormSubmissionChecklistStateInfo.Completed;
        break;

      case WorkflowFormSubmissionChecklistStateEnum.NotApplicable:
        stateInfo = WorkflowFormSubmissionChecklistStateInfo.NotApplicable;
        break;

      case WorkflowFormSubmissionChecklistStateEnum.InProgress:
      default:
        stateInfo = WorkflowFormSubmissionChecklistStateInfo.InProgress;
    }
    return stateInfo;
  }

  static getWorkflowFormSubmissionChecklistStateInfoList(): IWorkflowFormSubmissionChecklistStateInfo[] {
    return [
      WorkflowFormSubmissionChecklistStateInfo.InProgress,
      WorkflowFormSubmissionChecklistStateInfo.Completed,
      WorkflowFormSubmissionChecklistStateInfo.NotApplicable
    ];
  }
}

export class WorkflowFormSubmissionChecklistToDoTemplate
  implements IWorkflowFormSubmissionChecklistToDoTemplate {
  readonly id?: number;
  readonly todo_text: string;
  readonly todo_department: number | IDepartment;

  constructor(data: IWorkflowFormSubmissionChecklistToDoTemplateData) {
    if (data.id) {
      this.id = data.id;
    }

    this.todo_text = data.todo_text;

    if (typeof data.todo_department == 'number') {
      this.todo_department = data.todo_department;
    } else {
      this.todo_department = new Department(data.todo_department);
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class WorkflowFormSubmissionComment extends MentionCommentMixin
  implements IWorkflowFormSubmissionComment {
  readonly id?: number;
  readonly owner?: string;
  // not readonly as value would have to be set by component when parsing back to mention html
  text: string;
  readonly created_at?: Date | string;
  readonly updated_at?: Date | string;
  readonly attachments: IWorkflowFormSubmissionCommentAttachment[] = [];

  constructor(data: IWorkflowFormSubmissionCommentData) {
    super(data);
    if (data.id) {
      this.id = data.id;
    }

    if (data.owner) {
      this.owner = data.owner;
    }

    this.text = data.text;

    if (data.created_at) {
      this.created_at = data.created_at;
    }

    if (data.updated_at) {
      this.updated_at = data.updated_at;
    }

    if (data.attachments) {
      this.attachments = data.attachments.map(
        attachment => new WorkflowFormSubmissionCommentAttachment(attachment)
      );
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  get isCommentEdited(): boolean {
    //milliseconds is different for both fields
    return (
      moment(this.created_at).format('YYYY-MM-DDTHH:mm:ss') !==
      moment(this.updated_at).format('YYYY-MM-DDTHH:mm:ss')
    );
  }
}
export class WorkflowFormSubmissionFieldError
  implements IWorkflowFormSubmissionFieldError {
  readonly form: string[] = [];
  readonly owner: string[] = [];
  readonly answers: WorkflowFormFieldSubmissionDataFieldError[] = [] as WorkflowFormFieldSubmissionDataFieldError[];

  constructor(data?: IWorkflowFormSubmissionFieldErrorData) {
    if (data) {
      if (data.form) {
        this.form = data.form;
      }
      if (data.owner) {
        this.owner = data.owner;
      }
      if (data.answers) {
        this.answers = data.answers.map(
          answer => new WorkflowFormFieldSubmissionDataFieldError(answer)
        );
      }
    }
  }

  clearErrors(): void {
    this.form.splice(0, this.form.length);
    this.owner.splice(0, this.owner.length);
    this.answers.splice(0, this.answers.length);
  }

  static isWorkflowFormSubmissionFieldError(
    data: IWorkflowFormSubmissionFieldErrorData
  ): boolean {
    if (data.form && Array.isArray(data.form)) {
      return true;
    } else if (data.owner && Array.isArray(data.owner)) {
      return true;
    } else if (data.answers && Array.isArray(data.answers)) {
      return true;
    }
    return false;
  }
}

export class WorkflowFormFieldSubmissionDataFieldError
  implements IWorkflowFormFieldSubmissionDataFieldError {
  readonly value: string[] = [];
  readonly field: string[] = [];
  readonly option_submissions: WorkflowFormFieldOptionSubmissionFieldError[] = [] as WorkflowFormFieldOptionSubmissionFieldError[];

  constructor(data?: IWorkflowFormFieldSubmissionDataFieldErrorData) {
    if (data) {
      if (data.value) {
        this.value = data.value;
      }
      if (data.field) {
        this.field = data.field;
      }
      if (data.option_submissions) {
        this.option_submissions = data.option_submissions.map(
          entitlement =>
            new WorkflowFormFieldOptionSubmissionFieldError(entitlement)
        );
      }
    }
  }

  clearErrors(): void {
    this.value.splice(0, this.value.length);
    this.field.splice(0, this.field.length);
    this.option_submissions.splice(0, this.field.length);
  }

  static isWorkflowFormFieldSubmissionDataFieldError(
    data: IWorkflowFormFieldSubmissionDataFieldErrorData
  ): boolean {
    if (data.value && Array.isArray(data.value)) {
      return true;
    } else if (data.field && Array.isArray(data.field)) {
      return true;
    } else if (
      data.option_submissions &&
      Array.isArray(data.option_submissions)
    ) {
      return true;
    }
    return false;
  }
}

export class WorkflowFormFieldOptionSubmissionFieldError
  implements IWorkflowFormFieldOptionSubmissionFieldError {
  readonly option: string[] = [];
  readonly non_field_errors: string[] = [];

  constructor(data?: IWorkflowFormFieldOptionSubmissionFieldErrorData) {
    if (data) {
      if (data.option) {
        this.option = data.option;
      }
      if (data.non_field_errors) {
        this.non_field_errors = data.non_field_errors;
      }
    }
  }

  clearErrors(): void {
    this.option.splice(0, this.option.length);
    this.non_field_errors.splice(0, this.non_field_errors.length);
  }

  static isWorkflowFormFieldOptionSubmissionFieldErro(
    data: IWorkflowFormFieldOptionSubmissionFieldErrorData
  ): boolean {
    if (data.option && Array.isArray(data.option)) {
      return true;
    }
    if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
      return true;
    }
    return false;
  }
}

export class WorkflowFormSubmissionCommentFieldError
  implements IWorkflowFormSubmissionCommentFieldError {
  readonly text: string[] = [];

  constructor(data: IWorkflowFormSubmissionCommentFieldErrorData) {
    if (data) {
      if (data.text) {
        this.text = data.text;
      }
    }
  }

  clearErrors(): void {
    this.text.splice(0, this.text.length);
  }
}

export class WorkflowFormSubmissionCommentAttachment extends AbstractModel
  implements IWorkflowFormSubmissionCommentAttachment {
  readonly id?: number | undefined;
  readonly file: string | File;
  readonly filename: string = '';

  constructor(data: IWorkflowFormSubmissionCommentAttachmentData) {
    super();

    const requiredFields: (keyof IWorkflowFormSubmissionCommentAttachmentData)[] = [
      'file'
    ];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    if (data.filename) {
      this.filename = data.filename;
    }

    this.file = data.file!;
  }

  toString() {
    return this.filename;
  }
}
